import ContextualSaveBar from "@/components/bars/ContextualSaveBar";
import { Card, CardContent, CardHeader } from "@/components/layouts/Card";
import PageLoader from "@/components/loader/PageLoader";
import OptionSelectionModal from "@/components/modals/OptionSelectionModal";
import ReorderableList from "@/components/modals/ReorderableList";
import { useUser } from "@/contexts/UserContext";
import { Option, OptionList } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

const EditOptionList = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { id: optionListId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [optionList, setOptionList] = useState<OptionList>({
    id: 0,
    name: "",
    options: [],
    required: false,
    option_type: "increment_decrement",
  });
  const [originalOptionList, setOriginalOptionList] =
    useState<OptionList | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);

  useEffect(() => {
    const fetchOptionList = async () => {
      try {
        if (!loading && axiosInstance && optionListId) {
          const apiUrl =
            import.meta.env.VITE_APP_REDBIRDPOSBE_OPTIONLIST_INFORMATION ?? "";
          const response = await axiosInstance.get(`${apiUrl}${optionListId}/`);
          setOptionList(response.data);
          setOriginalOptionList(response.data);
        }
      } catch (error) {
        console.error("Error fetching option list:", error);
        toast.error("Failed to fetch option list details.");
      }
    };

    if (optionListId) {
      fetchOptionList();
    }
  }, [axiosInstance, loading, optionListId]);

  useEffect(() => {
    if (originalOptionList) {
      const isChanged =
        JSON.stringify(optionList) !== JSON.stringify(originalOptionList);
      setIsDirty(isChanged);
    }
  }, [originalOptionList, optionList]);

  const handleRemoveOption = (option: Option) => {
    setOptionList((prev) => ({
      ...prev,
      options: prev.options.filter((opt) => opt.id !== option.id),
    }));
    setIsDirty(true);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      if (!loading && axiosInstance && user?.selectedBranch) {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_OPTIONLIST_INFORMATION ?? "";
        const updatedOptionList = {
          name: optionList.name,
          branch: user.selectedBranch.id,
          options: optionList.options.map((option) => option.id),
          required: optionList.required,
          option_type: optionList.option_type,
          max_selections: optionList.max_selections,
        };
        const response = await axiosInstance.put(
          `${apiUrl}${optionListId}/`,
          updatedOptionList,
        );
        setOptionList(response.data);
        setOriginalOptionList(response.data);
        setIsDirty(false);
        toast.success("Option list updated successfully");
        navigate("/menu/option-lists");
      }
    } catch (error) {
      console.error("Error updating option list:", error);
      toast.error("Failed to update option list");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (originalOptionList) {
      setOptionList(originalOptionList);
      setIsDirty(false);
    }
  };

  const handleOptionSave = (selectedOptions: Option[]) => {
    const updatedOptionLists = selectedOptions.map((option) => {
      const existingOptions = selectedOptions.find(
        (existingOption) => existingOption.id === option.id,
      );
      return existingOptions ? { ...existingOptions, ...option } : option;
    });
    setOptionList((prev) => ({
      ...prev,
      options: updatedOptionLists,
    }));
    setIsOptionModalOpen(false);
    setIsDirty(true);
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <h1 className="mb-4 text-xl font-bold">{t("Edit Option List")}</h1>
          <Card className="mb-4">
            <CardHeader>
              <h2 className="text-lg font-semibold">
                {t("General Information")}
              </h2>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="name"
                >
                  {t("Option List Name")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={optionList.name}
                  onChange={(e) =>
                    setOptionList({ ...optionList, name: e.target.value })
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                />
              </div>
              <div className="mb-4">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="optionType"
                >
                  {t("Option Type")}
                </label>
                <select
                  id="optionType"
                  value={optionList.option_type}
                  onChange={(e) =>
                    setOptionList({
                      ...optionList,
                      option_type: e.target.value,
                      max_selections: null,
                    })
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                >
                  <option value="multiple_choice">
                    {t("Multiple Choice")}
                  </option>
                  <option value="checkbox">{t("Checkbox")}</option>
                  <option value="increment_decrement">
                    {t("Increment/Decrement")}
                  </option>
                </select>
              </div>
              {(optionList.option_type === "checkbox" ||
                optionList.option_type === "increment_decrement") && (
                <div className="mb-4">
                  <label
                    className="mb-2 block text-sm font-bold text-gray-700"
                    htmlFor="maxSelections"
                  >
                    {t("Max Selections")}
                  </label>
                  <input
                    type="number"
                    id="maxSelections"
                    value={optionList.max_selections ?? ""}
                    onChange={(e) =>
                      setOptionList({
                        ...optionList,
                        max_selections: parseInt(e.target.value) || null,
                      })
                    }
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                  />
                </div>
              )}
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={optionList.required}
                    onChange={(e) =>
                      setOptionList({
                        ...optionList,
                        required: e.target.checked,
                      })
                    }
                    className="form-checkbox h-5 w-5 text-red-600"
                  />
                  <span className="ml-2 text-gray-700">{t("Required")}</span>
                </label>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-4">
            <CardHeader className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t("Linked Options")}</h2>
              <button
                type="button"
                onClick={() => setIsOptionModalOpen(true)}
                className="flex items-center rounded-md bg-gray-500 px-3 py-1 text-white"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t("Edit")}
              </button>
            </CardHeader>
            <CardContent>
              <ReorderableList
                items={optionList.options}
                onReorder={(newOptions) => {
                  setOptionList((prev) => ({
                    ...prev,
                    options: newOptions,
                  }));
                  setIsDirty(true);
                }}
                onRemove={handleRemoveOption}
                renderItem={(option) => option.name}
              />
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="mt-4 flex justify-end">
            <Link
              to="/menu/option-lists"
              className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
            >
              {t("Cancel")}
            </Link>
            <button
              type="submit"
              className="rounded-md bg-red-500 px-4 py-2 text-white"
              disabled={isSaving}
            >
              {t("Update")}
            </button>
          </div>
        </form>

        <OptionSelectionModal
          isOpen={isOptionModalOpen}
          onClose={() => setIsOptionModalOpen(false)}
          onSave={handleOptionSave}
          initialSelectedOptions={optionList.options}
        />

        <ContextualSaveBar
          isDirty={isDirty}
          onSave={handleSubmit}
          onDiscard={handleDiscard}
          isSaving={isSaving}
        />
      </div>
    </PageLoader>
  );
};

export default EditOptionList;
